import * as THREE from 'three';
import type { IReactionDisposer } from 'mobx';
import { reaction } from 'mobx';
import type { IUiThreeDPresentation, IPresentationChangeHandler, IDrawer } from './Interfaces';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { ICenterLineViewModel } from '@/engine/app/interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { IImagesPortContext } from '@/engine/app/modules/images/interfaces';

export class CenterlineImagesPresentation implements IUiThreeDPresentation {
  private _lines: Array<THREE.Line> = [];

  private _drawer: IDrawer;

  private readonly _object3D: THREE.Object3D;

  private _changeHandler: IPresentationChangeHandler;

  private readonly _selectionReactionDisposer: IReactionDisposer;

  private _viewModel: ICenterLineViewModel;

  private _planningAlgorithm: PlanningAlgorithm;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  private _model: DT.Centerline | undefined;

  private readonly _color: { color: string };

  private _sliceCS: THREE.Matrix4 | undefined;

  private _imagesPortContext: IImagesPortContext;

  public constructor(
    imagesPortContext: IImagesPortContext,
    drawer: IDrawer,
    planningAlgorithm: PlanningAlgorithm,
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler
  ) {
    this._imagesPortContext = imagesPortContext;

    this._viewModel = viewModel;
    this._color = { color: this._viewModel.selectionColor };
    this._planningAlgorithm = planningAlgorithm;
    this._drawer = drawer;
    this._object3D = new THREE.Object3D();
    this._object3D.visible = viewModel.isSelected;
    this._changeHandler = changeHandler;

    this._model = this._planningAlgorithm.getValue(this._viewModel.descriptor, DT.Centerline);

    this._subscriptions.add(
      this._imagesPortContext.addOnSliceCSChangedListener(this._updateAppearance.bind(this), true)
    );

    this._selectionReactionDisposer = reaction(
      () => viewModel.isSelected,
      (selection) => {
        this._object3D.visible = selection;
        this._changeHandler.handlePresentationChange();
      }
    );

    this._subscriptions.add(
      this._planningAlgorithm.addOnChangedCallbackFor<DT.Centerline>(
        this._viewModel.descriptor,
        this._onModelChanged.bind(this)
      )
    );
  }

  private _createPresentation(): void {
    if (!this._model || !this._sliceCS) return;

    const plane = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
    plane.applyMatrix4(this._sliceCS);

    const points = [...this._model.value.points];

    for (let i = 0; i < points.length - 1; i++) {
      const pointFirst = points[i];
      const pointSecond = points[i + 1];

      const distanceToFirstPoint = Math.abs(plane.distanceToPoint(pointFirst));
      const pointFirstOpacity = this._getOpacityByDistance(distanceToFirstPoint);

      const distanceToSecondPoint = Math.abs(plane.distanceToPoint(pointSecond));
      const pointSecondOpacity = this._getOpacityByDistance(distanceToSecondPoint);

      const lineOpacity = (pointFirstOpacity + pointSecondOpacity) / 2;

      if (lineOpacity === 0) continue;

      const line = this._drawer.createLine([pointFirst, pointSecond], this._color, lineOpacity);
      line.renderOrder = 1;
      this._lines.push(line);
      this._object3D.add(line);
      this._object3D.visible = this._viewModel.isVisible && this._viewModel.isSelected;
    }
  }

  private _clearPresentation(): void {
    this._lines.forEach((line) => {
      this._object3D.remove(line);
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    });
    this._lines = [];
  }

  public get3dObject(): THREE.Object3D {
    return this._object3D;
  }

  private _onModelChanged(model?: DT.Centerline): void {
    this._model = model;
    this._clearPresentation();
    this._createPresentation();
    this._changeHandler.handlePresentationChange();
  }

  private _updateAppearance(sliceCS: THREE.Matrix4): void {
    this._sliceCS = sliceCS;
    this._clearPresentation();
    this._createPresentation();
    this._changeHandler.handlePresentationChange();
  }

  private _getOpacityByDistance(distanceToPoint: number): number {
    const maxDistance = this._imagesPortContext.sliceDistance * 2;

    let relDist = distanceToPoint / maxDistance;

    if (relDist > 0.75) relDist = 0.75;
    if (relDist < 0.25) relDist = 0;

    return 1 - relDist;
  }

  public dispose(): void {
    this._selectionReactionDisposer();
    this._subscriptions.dispose();
    this._lines.forEach((line) => {
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    });
  }
}
